import React, { useMemo, useState } from "react";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Box, Button, Tooltip } from "@mui/material";
import { useCharacteristics, useMeasures } from "hooks";
import { useDealActions, useReportsActions } from "store";
import { ChartSettings, ChartType, ReportsQueries } from "store/types";
import { CHART_BIN_BY_MEASURE_PREFIX, normalizeGroupByCharacteristic } from "utils";

import { AutocompleteMultiSelect, SelectOption } from "../AutocompleteMultiSelect/AutocompleteMultiSelect";

interface Props {
  name: string;
  chartProps: ChartSettings;
  onClose: () => void;
  isInvestorView?: boolean;
}

type GroupByOption = { key: string; text: string; value: string; group: "Characteristics" | "Measures" };

const CHARACTERISTIC_ANCHORS: GroupByOption[] = [
  { key: "Investor", text: "Investor", value: "investor", group: "Characteristics" },
  { key: "Deal", text: "Deal", value: "deal", group: "Characteristics" }
];

const GRAPH_TYPE_OPTIONS: SelectOption<ChartType>[] = [
  {
    key: "pie",
    value: "pie",
    text: "Pie"
  },
  {
    key: "bar",
    value: "bar",
    text: "Bar"
  },
  {
    key: "column",
    value: "column",
    text: "Column"
  },
  {
    key: "barStacked",
    value: "barStacked",
    text: "Stacked Bar"
  },
  {
    key: "columnStacked",
    value: "columnStacked",
    text: "Stacked Column"
  },
  {
    key: "waterfall",
    value: "waterfall",
    text: "Waterfall"
  },
  {
    key: "top5",
    value: "top5",
    text: "Top 5"
  },
  {
    key: "scatter",
    value: "scatter",
    text: "X/Y Scatter"
  },
  {
    key: "clusterBar",
    value: "clusterBar",
    text: "Cluster Bar"
  }
];

export const ChartMenu: React.FC<Props> = ({ name, chartProps, onClose, isInvestorView }) => {
  const characteristicsAll = useCharacteristics();
  const groupedValues = useMeasures(false);
  const { setGraphDataFormatAction } = useReportsActions();
  const { setInvestorsViewChart } = useDealActions();

  const { chartType, measure, groupBy, xAxis, yAxis } = chartProps;

  const [state, setState] = useState<Partial<ReportsQueries["graphDataFormation"][string]>>({
    chartType,
    measure,
    groupBy: groupBy?.startsWith(CHART_BIN_BY_MEASURE_PREFIX) ? "self" : groupBy,
    xAxis,
    yAxis
  });

  const groupByOptions = useMemo((): GroupByOption[] => {
    const chars: GroupByOption[] = [...CHARACTERISTIC_ANCHORS];
    characteristicsAll.forEach((c) => {
      if (typeof c === "string") {
        chars.push({
          key: c,
          text: c,
          value: normalizeGroupByCharacteristic(c),
          group: "Characteristics"
        });
        return;
      }
      if (typeof c === "object" && c.role === "category") {
        return;
      }
      if (typeof c === "object") {
        chars.push({
          text: c.text,
          key: c.text,
          value: normalizeGroupByCharacteristic(c.text),
          group: "Characteristics"
        });
      }
    });

    if (state.chartType === "scatter") {
      return chars;
    }

    const measures: GroupByOption[] = [
      {
        key: "Self",
        text: "Self",
        value: "self",
        group: "Measures"
      }
    ];

    return [...measures, ...chars];
  }, [characteristicsAll, state.chartType]);

  const handleSubmit = () => {
    let resolvedGroupBy = state.groupBy;
    if (resolvedGroupBy === "self") {
      const normalizedMeasure = (state.measure ?? "").replace(/^calc-field-/, "");
      resolvedGroupBy = `${CHART_BIN_BY_MEASURE_PREFIX}${normalizedMeasure}`;
    }
    const payload = { key: name, ...state, groupBy: resolvedGroupBy };
    if (isInvestorView) {
      setInvestorsViewChart(payload);
    } else {
      setGraphDataFormatAction(payload);
    }
    onClose();
  };

  const defaultValue = useMemo(() => {
    return groupedValues.find((measure) => {
      if (typeof measure === "object") {
        return measure.key === "Adjusted NAV";
      }
      return measure === "Adjusted NAV";
    });
  }, [groupedValues]);

  const defaultGroupBy = useMemo(() => {
    return groupByOptions.find((o) => o.text === "Investor") ?? undefined;
  }, [groupByOptions]);

  return (
    <>
      <AutocompleteMultiSelect
        defaultValue={state.chartType}
        label="Chart Type"
        stateAfterClear={GRAPH_TYPE_OPTIONS[0]}
        options={GRAPH_TYPE_OPTIONS}
        onChange={(value) => {
          setState({ ...state, chartType: value as ChartType });
        }}
      />
      {state.chartType !== "scatter" && (
        <AutocompleteMultiSelect
          defaultValue={state.measure}
          label="Value"
          options={groupedValues}
          stateAfterClear={defaultValue}
          onChange={(value) => {
            setState({ ...state, measure: (value as string) || "" });
          }}
        />
      )}
      {state.chartType === "scatter" && (
        <AutocompleteMultiSelect
          defaultValue={state.xAxis?.name}
          label="xAxis"
          options={groupedValues}
          stateAfterClear={defaultValue}
          onChange={(value) => {
            setState({ ...state, xAxis: { type: "", name: (value as string) || "" } });
          }}
        />
      )}
      {state.chartType === "scatter" && (
        <AutocompleteMultiSelect
          defaultValue={state.yAxis?.name}
          label="yAxis"
          options={groupedValues}
          stateAfterClear={defaultValue}
          onChange={(value) => {
            setState({ ...state, yAxis: { type: "", name: (value as string) || "" } });
          }}
        />
      )}
      <AutocompleteMultiSelect
        defaultValue={state.groupBy}
        label="Group By"
        stateAfterClear={defaultGroupBy}
        options={groupByOptions}
        groupBy={(option) => (option as GroupByOption).group}
        onChange={(value) => {
          setState({ ...state, groupBy: (value as string) || "" });
        }}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            {(option as GroupByOption).text}
            {(option as GroupByOption).text === "Self" && (
              <Tooltip title="Creates groups by binning the deals based on the given parameter" placement="right">
                <HelpOutlineIcon sx={{ ml: 0.5, fontSize: 14, color: "text.secondary", cursor: "help" }} />
              </Tooltip>
            )}
          </Box>
        )}
      />
      {/* <Autocomplete<GroupByOption, false, false, false>
        options={groupByOptions}
        groupBy={(option) => option.group}
        defaultValue={defaultGroupBy}
        stateAfterClear={defaultGroupBy}
        getOptionLabel={(o) => o.text}
        value={selecteGroupBy}
        onChange={(_, v) => setState({ ...state, groupBy: v?.value ?? "" })}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        renderOption={(props, option) => (
          <Box component="li" {...props}>
            {option.text}
            {option.text === "Self" && (
              <Tooltip title="It creates groups by binning the deals based on the given parameter" placement="right">
                <HelpOutlineIcon sx={{ ml: 0.5, fontSize: 14, color: "text.secondary", cursor: "help" }} />
              </Tooltip>
            )}
          </Box>
        )}
        renderInput={(params) => <TextField {...params} label="Group By" />}
      /> */}
      <Button sx={{ alignSelf: "flex-start" }} variant="contained" onClick={handleSubmit}>
        Apply
      </Button>
    </>
  );
};
